import React from "react";
import { PaymentHistory as PaymentHistoryType } from "src/shared/types";
import { statusColors } from "./OrderSidePanal";
import { themeStyles } from "src/shared/services/themes";
import { useUser } from "src/shared/context/UserContext";

const RETURN_SOURCES = new Set(["RETURN", "REFUND"]);

export const formatAmount = (val: any): string => {
  const num = Number(val);
  if (isNaN(num)) return "—";
  return Math.abs(num) < 0.01 ? "0.00" : num.toFixed(2);
};

const GreenAmount = ({ value }: { value: any }) => {
  const display = formatAmount(value);
  if (display === "0.00") return <span className="text-gray-400">0.00</span>;
  return <span className="text-green-600 font-medium">{display}</span>;
};

const RedAmount = ({ value }: { value: any }) => {
  const display = formatAmount(value);
  if (display === "0.00") return <span className="text-gray-400">0.00</span>;
  return <span className="text-red-500 font-medium">{display}</span>;
};

const cols = [
  { label: "#", cls: "w-8 text-center" },
  { label: "Date", cls: "w-36" },
  { label: "Source", cls: "w-28" },
  { label: "Method", cls: "w-24" },
  { label: "Received", cls: "w-24 text-right" },
  { label: "Refund", cls: "w-24 text-right" },
  { label: "Adjust", cls: "w-20 text-right" },
  { label: "Amount Due", cls: "w-24 text-right" },
];

function PaymentHistory({
  isOpen,
  onClose,
  paymentHistory,
}: {
  isOpen: boolean;
  onClose: () => void;
  paymentHistory: PaymentHistoryType[];
}) {
  const { user } = useUser();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden">

        {/* Header */}
        <div className={`flex justify-between items-center px-4 py-2 ${themeStyles[user?.themes?.backgroundColour ?? 0]}`}>
          <h2 className="text-2xl font-semibold">Payment History</h2>
          <button onClick={onClose} className="text-2xl leading-none font-semibold hover:opacity-70">&times;</button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto max-h-[65vh]">
          {paymentHistory.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No payment history found.</p>
          ) : (
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead className="sticky top-0 bg-gray-100 text-gray-500 uppercase text-xs">
                <tr>
                  {cols.map(({ label, cls }) => (
                    <th key={label} className={`px-3 py-2 font-medium ${cls}`}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((p, idx) => {
                  const isReturn = RETURN_SOURCES.has(p.paymentSource?.toUpperCase());
                  const date = p.paidOn?.$date
                    ? new Date(p.paidOn.$date).toLocaleDateString("en-GB", {
                      timeZone: "UTC",
                    })
                    : "—";

                  const time = p.paidOn?.$date
                    ? new Date(p.paidOn.$date).toLocaleTimeString("en-GB", {
                      timeZone: "UTC",
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })
                    : "";

                  return (
                    <tr
                      key={p._id?.$oid ?? idx}
                      className={`border-b border-gray-100 ${isReturn ? "bg-red-50" : "hover:bg-gray-50"}`}
                    >
                      {/* # */}
                      <td className="px-3 py-2 text-center text-gray-400">{idx + 1}</td>

                      {/* Date */}
                      <td className="px-3 py-2">
                        <p className="text-gray-700 leading-tight">{date}</p>
                        <p className="text-gray-400 text-xs">{time}</p>
                      </td>

                      {/* Source */}
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap
                          ${statusColors[p.paymentSource] ?? statusColors[p.paymentSource?.toUpperCase()] ?? "bg-gray-100 text-gray-600"}`}>
                          {p.paymentSource?.replace(/_/g, " ")}
                        </span>
                      </td>

                      {/* Method */}
                      <td className="px-3 py-2 text-gray-600">{p.paymentMethod ?? "—"}</td>

                      {/* Received — green, only for non-return rows */}
                      <td className="px-3 py-2 text-right">
                        {!isReturn ? <GreenAmount value={p.receivedAmount} /> : <span className="text-gray-500">0.00</span>}
                      </td>

                      {/* Refund — red, only for return rows */}
                      <td className="px-3 py-2 text-right">
                        {isReturn ? <RedAmount value={p.receivedAmount} /> : <span className="text-gray-500">0.00</span>}
                      </td>

                      {/* Adjust */}
                      <td className="px-3 py-2 text-right text-gray-500">
                        {formatAmount(p.adjustAmount)}
                      </td>

                      {/* Amount Due */}
                      <td className="px-3 py-2 text-right text-gray-500">
                        {p.amountDue != null ? formatAmount(p.amountDue) : "0.00"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition">
            Close
          </button>
        </div>

      </div>
    </div>
  );
}

export default PaymentHistory;